import React, { Component } from "react";
import PropTypes from "prop-types";
import ReactModal from "react-modal";
import styled from "styled-components";
import { CloseRounded } from "@mui/icons-material";
import { IconButton } from "@mui/material";

import { H3 } from "./styled/H3";
import { slideInFade, fadeIn } from "./Animations";

export class Modal extends Component {
    componentDidMount() {
        const rootElement = this.props.rootElement;

        if (document && document.getElementById(rootElement)) {
            ReactModal.setAppElement(`#${rootElement}`);
        } else {
            ReactModal.setAppElement(document.body);
        }
    }

    render() {
        return (
            <StyledModal
                isOpen={this.props.isOpen}
                onRequestClose={this.props.onRequestClose}
                contentLabel={this.props.contentLabel}
            >
                <StyledIconButton onClick={this.props.onRequestClose} aria-label="Close modal">
                    <CloseRounded
                        data-testid="modal-close-button"
                        aria-hidden="true"
                        fontSize="large"
                    />
                </StyledIconButton>
                <ModalHeader>
                    <H3Styled data-testid="modal-title">{this.props.contentLabel}</H3Styled>
                </ModalHeader>
                <ModalBody>{this.props.children}</ModalBody>
            </StyledModal>
        );
    }
}

const ReactModalAdapter = ({ className, ...props }) => {
    const contentClassName = `${className}__content`;
    const overlayClassName = `${className}__overlay`;
    return (
        <ReactModal
            portalClassName={className}
            className={contentClassName}
            overlayClassName={overlayClassName}
            {...props}
        />
    );
};

const StyledIconButton = styled(IconButton)`
    && {
        position: absolute;
        right: -1.8rem;
        top: -1.8rem;
        background-color: ${props => props.theme.primary};
        color: white;

        &:hover {
            background-color: ${props => props.theme.primary};
        }
    }
`;

const StyledModal = styled(ReactModalAdapter)`
    &__overlay {
        align-items: center;
        animation: ${fadeIn} 0.3s ease-out;
        background-color: rgba(32, 40, 46, 0.5);
        display: flex;
        height: 100vh;
        justify-content: center;
        position: fixed;
        top: 0;
        width: 100vw;
        z-index: 3;

        @media (prefers-reduced-motion) {
            animation: none;
        }
    }

    &__content {
        animation: ${slideInFade} 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        background: #fff;
        border-radius: 1rem;
        height: auto;
        font-size: 1.2rem;
        max-height: 70rem;
        max-width: 60rem;
        min-width: 30rem;
        outline: none;
        position: relative;
        width: auto;

        @media (prefers-reduced-motion) {
            animation: none;
        }

        @media (max-width: ${props => props.theme.maxWidth}) {
            max-width: calc(100vw - 2rem);
        }

        &:before {
            content: " ";
            border-radius: 1rem 1rem 0 0;
            position: absolute;
            top: 0;
            height: 1rem;
            width: 100%;
            background-color: ${props => props.theme.primary};
        }
    }
`;

export const H3Styled = styled(H3)`
    margin: 0;
`;

const ModalHeader = styled.div`
    overflow: auto;
    padding: 3rem 2rem 0;
    height: 6rem;
    width: 100%;
`;

const ModalBody = styled.div`
    padding: 1rem 2rem 2rem;
    overflow-y: scroll;
    max-height: 63rem;
`;

Modal.defaultProps = {
    rootElement: "root"
};

Modal.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    contentLabel: PropTypes.string,
    isOpen: PropTypes.bool,
    onRequestClose: PropTypes.func,
    rootElement: PropTypes.string
};

ReactModalAdapter.propTypes = {
    className: PropTypes.string
};
